/**
 * Compact tenant identification badge for the application header.
 *
 * Shows: tenant display name · region · sister-count.
 * Renders nothing while the tenant query is loading or when not authenticated
 * (the header itself already gates by isAuthenticated).
 */
import React from 'react'
import { Building2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { useMyTenant } from '@/platform/tenant/useMyTenant'

export const TenantBadge = (): JSX.Element | null => {
  const { data, isLoading, error } = useMyTenant()
  if (isLoading || error || !data) return null

  const sisters = data.relationships.filter((r) => r.relationshipType === 'sister').length
  const tooltip = `${data.tenant.slug} · ${data.tenant.region}${
    sisters > 0 ? ` · ${sisters} sister tenant${sisters === 1 ? '' : 's'}` : ''
  }`

  return (
    <Link
      to="/admin/tenant"
      title={tooltip}
      className="hidden md:flex items-center gap-2 text-sm rounded-md px-2 py-1 hover:bg-muted"
    >
      <Building2 className="h-4 w-4 text-muted-foreground" />
      <span className="font-medium">{data.tenant.name}</span>
      <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
        {data.tenant.region}
      </Badge>
      {sisters > 0 && (
        <Badge variant="secondary" className="text-[10px]">
          +{sisters}
        </Badge>
      )}
    </Link>
  )
}
